import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useLocale } from "@genius-tracker/i18n/react";
import type { SessionInfo } from "../lib/api.js";
import { computeMenuPosition, type MenuPosition } from "./accountMenuPosition.js";

export { computeMenuPosition } from "./accountMenuPosition.js";

/**
 * Sprint 55.3 — Main App account menu.
 *
 * Port of `apps/admin/src/components/AccountMenu.tsx` so the Main
 * App's top bar carries the same avatar + popover the Admin
 * Console has. The Owner reported that the customer-app top bar
 * shows the user's display name + role badge as inline text, which
 * is redundant — that info belongs inside a popover behind the
 * avatar, freeing the chrome of duplicate identity surfaces.
 *
 * What lives where
 * ================
 *
 * - Avatar trigger: a circular button with the user's initials.
 *   Replaces the prior `RoleChip + displayName` inline text.
 * - Popover contents (seit E-BUNDLE-16 in `AccountMenuPanel.tsx`):
 *     • SIGNED IN AS (label)
 *     • email
 *     • display name (if set)
 *     • role pill (the highest tenant role the session carries)
 *     • Profile link → /me/profile
 *     • Notification settings link → /me/notifications (the account/
 *       settings home for personal delivery channels)
 *     • Sign out
 *
 * Safety
 * ======
 *
 * - Renders only `displayName` and `email` from the session. Never
 *   exposes JWT / cookie / refresh token / `sub`.
 * - "Sign out" calls the existing `logout()` API helper and assigns
 *   the Keycloak `endSessionUrl`. No impersonation.
 *
 * Accessibility
 * =============
 *
 * - Trigger has `aria-haspopup="menu"` and `aria-expanded`.
 * - Popover has `role="menu"` and its actions are `role="menuitem"`.
 * - Escape closes the popover. Click outside closes the popover.
 */

/**
 * E-BUNDLE-16 — der Menueinhalt liegt in einem eigenen Modul und wird erst
 * geladen, wenn ihn jemand sehen kann. Begruendung und Messung stehen in
 * `AccountMenuPanel.tsx`.
 */
const AccountMenuPanel = lazy(async () => {
  const mod = await import("./AccountMenuPanel.js");
  return { default: mod.AccountMenuPanel };
});

let panelPrefetch: Promise<unknown> | null = null;

function prefetchAccountMenuPanel(): void {
  panelPrefetch ??= import("./AccountMenuPanel.js");
}

interface AccountMenuProps {
  readonly session: SessionInfo;
  readonly onSignOut?: (returnTo?: string) => Promise<string>;
  readonly onNavigate?: (url: string) => void;
}

export function AccountMenu({ session, onSignOut, onNavigate }: AccountMenuProps) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // GT-GI-SHELL-FINAL-VISUAL-AND-AI-FIDELITY-FIX-01 (DL-125) — the menu is
  // portaled to <body>, so the outside-click check must also see the portaled
  // menu node (it is no longer inside `containerRef`).
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onClick = (e: MouseEvent) => {
      const node = containerRef.current;
      const menu = menuRef.current;
      const target = e.target as Node;
      if ((node && node.contains(target)) || (menu && menu.contains(target))) return;
      close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return;
    }
    const recompute = () => {
      const node = triggerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      setPosition(
        computeMenuPosition(rect, {
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      );
    };
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  const display = session.identity.displayName?.trim() || session.identity.email;
  const initials = computeInitials(session.identity.displayName, session.identity.email);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onPointerEnter={prefetchAccountMenuPanel}
        onFocus={prefetchAccountMenuPanel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("web.account.menu_label")}
        title={display}
        style={triggerStyle}
        data-testid="web-account-menu-trigger"
      >
        <span aria-hidden="true" style={avatarStyle}>
          {initials}
        </span>
      </button>
      {open ? (
        <Suspense fallback={null}>
          <AccountMenuPanel
            session={session}
            position={position}
            menuRef={menuRef}
            onClose={close}
            onSignOut={onSignOut}
            onNavigate={onNavigate}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

function computeInitials(displayName: string | null, email: string): string {
  const source = displayName?.trim() || email;
  if (!source) return "?";
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const first = parts[0];
  const second = parts[1];
  if (!first) return source.charAt(0).toUpperCase();
  if (!second) return first.slice(0, 2).toUpperCase();
  return ((first.charAt(0) ?? "") + (second.charAt(0) ?? "")).toUpperCase();
}

const triggerStyle: CSSProperties = {
  appearance: "none",
  border: "1px solid var(--gt-sys-border)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  // DL-133 local tuning — pin to 32px (border-box) so the account avatar lines up
  // with the topbar pills + controls (all 32px) on one clean row.
  blockSize: 32,
  paddingBlock: 0,
  paddingInline: "var(--gt-sys-space-1)",
  borderRadius: "var(--gt-sys-radius-pill, 999px)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
};

const avatarStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  inlineSize: 28,
  blockSize: 28,
  borderRadius: "50%",
  background: "color-mix(in oklab, var(--gt-sys-accent) 14%, transparent)",
  color: "var(--gt-sys-accent)",
  font: "600 12px/1 system-ui, sans-serif",
  letterSpacing: 0.5,
};
